/**
 * PATH: packages/email-hub/src/feature/components/smart-action-bar/useSmartActionBarRuntime.ts
 * FILE_ID: EMAILHUB-SMARTACTIONBAR-RUNTIME-001
 * PURPOSE: Own every mutation SmartActionBar's disposition verbs trigger —
 *          the resolver model, busy-state, terminal-resolve, and the run()
 *          dispatcher — so the frame (SmartActionBar.tsx) stays hooks +
 *          guards + JSX only (500-line ceiling split, change #269 D4).
 * ROLE IN ARCHITECTURE: Concern hook below the SmartActionBar frame.
 * MAIN EXPORTS: useSmartActionBarRuntime.
 * NON-RESPONSIBILITIES: No JSX. Presentation lives in ActionButton.tsx /
 *   BillConfirmReject.tsx; the split-control Junk/Estimator affordances stay
 *   owned by JunkButton.tsx / EstimatorAllocator.tsx (never re-authored here).
 * NOTES FOR FUTURE AI: `resolve()` is the terminal-disposition contract
 *   (BUG history: operator 2026-06-17, "I clicked Junk yet it did not
 *   vanish and go to the next email") — every verb that removes the email
 *   from the inbox (bin / done / auto-trash) MUST call `resolve()`, never
 *   bare `onRefetch?.()`. Pinned by
 *   ../SmartActionBar.doneResolve.pin.test.ts, which reads THIS file for
 *   the resolve/run internals and SmartActionBar.tsx for the JSX wiring —
 *   keep both pin targets in sync with any further split.
 */
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useToast } from '../../../_internal/components/Toast'
import { useMarkEmailAsBill } from '../../../_stubs/bills/hooks/useBills'
import { useEmailActions, type Email } from '../../hooks/useEmails'
import {
  deriveHeaderActions,
  type HeaderActionKind,
  type HeaderActionsModel,
} from '../../lib/headerActions'
import { buildKanbanCardLink } from '../../lib/kanbanDeepLink'

export interface SmartActionBarRuntimeArgs {
  email: Email
  variant: 'all' | 'decision'
  onRefetch?: () => void
  onResolved?: (emailId: string) => void
  onOpenCard?: () => void
  onOpenBill?: () => void
}

export interface SmartActionBarRuntime {
  visibleModel: HeaderActionsModel
  /** "Done" reads accepted (green) only once archived or the linked card
   *  is DONE — grey otherwise (operator 2026-06-17). */
  emailDone: boolean
  busyKind: HeaderActionKind | null
  notBillBusy: boolean
  run: (kind: HeaderActionKind) => Promise<void>
  markNotBill: () => Promise<void>
}

/**
 * Derive the visible action model for `email` and wire every disposition
 * verb's mutation (Bills / bin / done / auto-trash / not-a-bill). Estimator
 * allocation and Junk are split-control affordances rendered by their own
 * components, so `run()` intentionally no-ops for those two kinds.
 *
 * Side effects: toasts, bulkAction/archive/createAutoJunkRule/setCategory
 * network calls, and `resolve()` (host callback) on every terminal verb.
 * Failure mode: each verb catches its own mutation and toasts an error;
 * `busyKind`/`notBillBusy` always reset via `finally`.
 */
export function useSmartActionBarRuntime({
  email,
  variant,
  onRefetch,
  onResolved,
  onOpenCard,
  onOpenBill,
}: SmartActionBarRuntimeArgs): SmartActionBarRuntime {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { bulkAction, createAutoJunkRule, archive, setCategory } = useEmailActions()
  const markAsBill = useMarkEmailAsBill()
  const [busyKind, setBusyKind] = useState<HeaderActionKind | null>(null)
  // The bill "✗ not a bill" reject runs through a different mutation
  // (re-classify, not send-to-bills) so it gets its own busy flag rather
  // than borrowing a HeaderActionKind slot.
  const [notBillBusy, setNotBillBusy] = useState(false)

  const model = deriveHeaderActions(email)
  const visibleModel: HeaderActionsModel =
    variant === 'decision'
      ? {
          primary:
            model.primary === 'send_to_bills' ||
            model.primary === 'allocate_estimator'
              ? model.primary
              : null,
          secondary: [],
        }
      : model

  const emailDone =
    email.is_archived === true ||
    email.allocation_summary?.card_status === 'DONE'

  // Terminal dispositions (Junk / Done / Bin / auto-trash) drop the email
  // from the inbox → advance the reader to the next message. Non-terminal
  // verbs (Bills / re-classify / open card-job) keep refetch-in-place.
  const resolve = useCallback(() => {
    if (onResolved) onResolved(email.id)
    else onRefetch?.()
  }, [onResolved, onRefetch, email.id])

  const run = useCallback(
    async (kind: HeaderActionKind) => {
      if (busyKind) return
      switch (kind) {
        case 'open_card': {
          if (onOpenCard) {
            onOpenCard()
            return
          }
          const link = buildKanbanCardLink(email.allocation_summary)
          if (link) navigate(link)
          return
        }
        case 'open_job': {
          if (email.job_id != null) navigate(`/jobs/${email.job_id}`)
          return
        }
        case 'send_to_bills': {
          setBusyKind(kind)
          try {
            const result = await markAsBill.run(email.id)
            if (!result) {
              toast(markAsBill.error || 'Could not allocate to Bills', 'error')
              return
            }
            toast(
              result.created
                ? 'Allocated to Bills - opening bill…'
                : 'Already in Bills - opening…',
              'success',
            )
            onRefetch?.()
            // The toast promised "opening bill…" — make it real: pop the
            // source invoice document in the right drawer (split-screen).
            onOpenBill?.()
          } finally {
            setBusyKind(null)
          }
          return
        }
        case 'move_to_trash': {
          setBusyKind(kind)
          try {
            // 'delete' is the soft-bin (status='binned') — the valid
            // backend bulk action. 'trash' is not a bulk action (the
            // ChoiceField rejects it), so the row was never binned.
            await bulkAction([email.id], 'delete')
            toast('Moved to bin', 'success')
            resolve()
          } catch {
            toast('Could not move to bin', 'error')
          } finally {
            setBusyKind(null)
          }
          return
        }
        case 'mark_done': {
          setBusyKind(kind)
          try {
            await archive(email.id, {
              folder: email.folder,
              isRead: email.is_read,
              isStarred: email.is_starred,
              // Done also flips the linked Kanban card to DONE (not just
              // archive/tombstone) so the board reflects completion.
              complete: true,
            })
            toast('Done - archived and card marked done', 'success')
            resolve()
          } catch {
            toast('Could not mark done', 'error')
          } finally {
            setBusyKind(null)
          }
          return
        }
        case 'auto_junk_rule': {
          setBusyKind(kind)
          try {
            const res = await createAutoJunkRule(email.id, 'sender_domain')
            toast(
              res.binned_count > 0
                ? `Auto-trash rule armed - binned ${res.binned_count} matching email${
                    res.binned_count === 1 ? '' : 's'
                  }`
                : 'Auto-trash rule armed for this sender domain',
              'success',
            )
            resolve()
          } catch {
            toast('Could not create auto-trash rule', 'error')
          } finally {
            setBusyKind(null)
          }
          return
        }
        case 'allocate_estimator':
          // Rendered as the EstimatorAllocator component, not a button.
          return
        case 'mark_junk':
          // Rendered as the JunkButton split control, which owns its
          // own bin + auto-junk-rule mutations — never routed here.
          return
      }
    },
    [busyKind, email, navigate, markAsBill, bulkAction, createAutoJunkRule, archive, toast, onRefetch, resolve, onOpenCard, onOpenBill],
  )

  // "✗ not a bill" — the operator overrides the AI's financial read.
  // Re-classify to the neutral `general` bucket (a locked override the
  // AI can't undo), which both drops the email out of the bill prompt
  // and stops it being auto-routed to AR/AP. Mirrors the list rail's
  // reject ✗, which also trains the matcher via a category correction.
  const markNotBill = useCallback(async () => {
    if (notBillBusy || busyKind) return
    setNotBillBusy(true)
    try {
      await setCategory(email.id, 'general')
      toast('Marked “not a bill”', 'success')
      onRefetch?.()
    } catch {
      toast('Could not update classification', 'error')
    } finally {
      setNotBillBusy(false)
    }
  }, [notBillBusy, busyKind, setCategory, email.id, toast, onRefetch])

  return { visibleModel, emailDone, busyKind, notBillBusy, run, markNotBill }
}
